import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Target, Flame, ArrowUpRight, Activity } from "lucide-react";
const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_ICON = {
    arjuna: Target,
    veda: Shield,
    kubera: Flame,
};

const PRODUCT_THEME = {
    arjuna: { // Gold
        hex: "#d4af37", hexHi: "#f0d060",
        border: "border-amber-300/40", borderHover: "hover:border-amber-300/60",
        bg: "bg-amber-300/5", text: "text-amber-200", textSoft: "text-amber-300",
        glow: "shadow-[0_0_30px_rgba(212,175,55,0.15)]",
        bar: "bg-amber-300",
    },
    veda: { // Emerald
        hex: "#10b981", hexHi: "#34d399",
        border: "border-emerald-400/40", borderHover: "hover:border-emerald-400/60",
        bg: "bg-emerald-400/5", text: "text-emerald-300", textSoft: "text-emerald-400",
        glow: "shadow-[0_0_30px_rgba(16,185,129,0.15)]",
        bar: "bg-emerald-400",
    },
    kubera: { // Violet
        hex: "#a855f7", hexHi: "#c084fc",
        border: "border-violet-400/40", borderHover: "hover:border-violet-400/60",
        bg: "bg-violet-400/5", text: "text-violet-300", textSoft: "text-violet-400",
        glow: "shadow-[0_0_30px_rgba(168,85,247,0.15)]",
        bar: "bg-violet-400",
    },
};

const STATUS_LABEL = {
    LIVE: "Live",
    LIVE_PAPER: "Live · paper",
    BUILDING: "In build",
};

const STATUS_TONE = {
    LIVE: "text-emerald-400 border-emerald-400/40 bg-emerald-400/5",
    LIVE_PAPER: "text-amber-300 border-amber-300/40 bg-amber-300/5",
    BUILDING: "text-neutral-400 border-neutral-500/40 bg-neutral-500/5",
};

export default function DfiPage() {
    const [products, setProducts] = useState([]);
    const [meta, setMeta] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.title = "DFI · Digital Finance Intelligence";
        fetch(`${API}/api/dfi/products`)
            .then((r) => r.json())
            .then((d) => {
                setProducts(d.products || []);
                setMeta({
                    entity: d.entity,
                    fee_model: d.fee_model,
                    custody: d.custody,
                    min_ticket_usdt: d.min_ticket_usdt,
                });
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    return (
        <div
            className="min-h-screen bg-neutral-950 text-neutral-100"
            data-testid="dfi-landing-page"
        >
            {/* Hero */}
            <div className="border-b border-neutral-800">
                <div className="max-w-6xl mx-auto px-6 py-20">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                        <div className="flex items-center gap-4 text-xs tracking-[0.3em] text-amber-300/80 uppercase">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/downloads/DFI_logo_final.png`} alt="DFI" className="w-28 h-28 object-contain drop-shadow-[0_0_30px_rgba(212,175,55,0.3)]" data-testid="dfi-hero-logo" />
                            <div>
                                <div className="text-amber-300 font-serif text-xl tracking-wide">DIGITAL FINANCE INTELLIGENCE</div>
                                <div className="text-neutral-500 italic text-xs tracking-widest mt-1">Global Asset Management</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-5">
                            <Link to="/dfi/veda" data-testid="dfi-nav-veda" className="text-[11px] tracking-[0.3em] uppercase text-amber-300/70 hover:text-amber-300">Veda</Link>
                            <Link to="/dfi/arjuna" data-testid="dfi-nav-arjuna" className="text-[11px] tracking-[0.3em] uppercase text-amber-300/70 hover:text-amber-300">Arjuna</Link>
                            <Link to="/dfi/kubera" data-testid="dfi-nav-kubera" className="text-[11px] tracking-[0.3em] uppercase text-amber-300/70 hover:text-amber-300">Kubera</Link>
                            <Link to="/dfi/orb" data-testid="dfi-nav-orb" className="text-[11px] tracking-[0.3em] uppercase text-amber-300 hover:text-amber-200 border border-amber-300/40 px-3 py-1 rounded-full">Orb ⬤</Link>
                            <Link
                                to="/dfi/admin"
                                data-testid="dfi-hero-admin-link"
                                className="text-[10px] tracking-[0.3em] uppercase text-neutral-600 hover:text-amber-300"
                            >
                                Operator console →
                            </Link>
                        </div>
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight max-w-3xl mt-10">
                        Three disciplined books.{" "}
                        <span className="text-amber-300/90">
                            One institutional-grade engine.
                        </span>
                    </h1>
                    <p className="mt-6 text-base max-w-2xl text-neutral-400 leading-relaxed">
                        Chart-honest crypto allocation across growth, yield,
                        and regime-shift sleeves. High-water-mark protected ·
                        segregated sub-account per client · insured capital.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-6 text-sm">
                        <div className="flex items-center gap-2 text-neutral-500">
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-300/80" />
                            Minimum ticket{" "}
                            <span className="text-neutral-200">
                                {(meta.min_ticket_usdt || 100000).toLocaleString()}{" "}
                                USDT
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-neutral-500">
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-300/80" />
                            30-day soft lock · monthly redemption
                        </div>
                        <div className="flex items-center gap-2 text-neutral-500">
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-300/80" />
                            Operator-controlled custody
                        </div>
                    </div>
                    <div className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 border border-amber-300/50 bg-amber-300/10 rounded-full" data-testid="hero-data-integrity-badge">
                        <Shield className="h-4 w-4 text-amber-300" />
                        <span className="text-sm tracking-[0.25em] uppercase text-amber-200 font-bold">Backtested · Hard-Tested Data · Fee-Adjusted</span>
                    </div>
                    <div className="mt-10">
                        <Link
                            to="/dfi/apply"
                            data-testid="dfi-hero-apply-cta"
                            className="inline-flex items-center gap-2 bg-amber-300 text-neutral-950 px-7 py-3 text-xs tracking-[0.2em] uppercase font-medium hover:bg-amber-200"
                        >
                            Apply to subscribe
                            <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                </div>
            </div>

            {/* Products grid */}
            <div className="max-w-6xl mx-auto px-6 py-20">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="text-xs tracking-[0.3em] text-neutral-500 uppercase mb-3">
                            Product menu
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-light">
                            Pick the book that matches your mandate.
                        </h2>
                    </div>
                    <div className="hidden sm:block text-xs text-neutral-500">
                        {products.length} active books
                    </div>
                </div>

                {loading ? (
                    <div
                        className="text-sm text-neutral-500"
                        data-testid="dfi-products-loading"
                    >
                        Loading product catalog…
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {products.map((p) => {
                            const key = p.name.toLowerCase();
                            const Icon = PRODUCT_ICON[key] || Target;
                            const theme = PRODUCT_THEME[key] || PRODUCT_THEME.arjuna;
                            return (
                                <article
                                    key={key}
                                    data-testid={`dfi-product-card-${key}`}
                                    className={`group border ${theme.border} bg-neutral-900/40 ${theme.borderHover} hover:bg-neutral-900/70 transition-all duration-300 p-7 flex flex-col relative overflow-hidden ${theme.glow}`}
                                >
                                    <div className={`absolute top-0 left-0 right-0 h-[2px] ${theme.bar}`} />
                                    <div className="flex items-start justify-between mb-6">
                                        <Icon className="h-6 w-6" style={{ color: theme.hex }} />
                                        <span
                                            className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${
                                                STATUS_TONE[p.status] ||
                                                STATUS_TONE.BUILDING
                                            }`}
                                        >
                                            {STATUS_LABEL[p.status] || p.status}
                                        </span>
                                    </div>
                                    <h3 className="text-4xl font-bold tracking-tight" style={{ color: theme.hexHi }}>
                                        {p.name}
                                    </h3>
                                    <p className={`mt-2 text-base italic font-semibold ${theme.textSoft}`}>
                                        {p.tagline}
                                    </p>
                                    <p className="mt-6 text-base text-neutral-300 leading-relaxed flex-1">
                                        {p.description}
                                    </p>
                                    {p.data_integrity && (
                                        <div className="mt-5 inline-flex items-center gap-2 px-3.5 py-2 border rounded-full self-start" style={{ borderColor: theme.hex, backgroundColor: `${theme.hex}15` }}>
                                            <Shield className="h-3.5 w-3.5" style={{ color: theme.hexHi }} />
                                            <span className="text-xs tracking-[0.22em] uppercase font-bold" style={{ color: theme.hexHi }}>{p.data_integrity}</span>
                                        </div>
                                    )}
                                    <dl className="mt-6 space-y-4 text-sm border-t border-neutral-800 pt-5">
                                        {p.live_nav_preview && p.live_nav_preview.nav_usd != null && (
                                            <div
                                                className="flex justify-between gap-4 pb-3 border-b border-neutral-800/60"
                                                data-testid={`dfi-product-nav-${key}`}
                                            >
                                                <dt className="text-neutral-400 uppercase tracking-wider flex items-center gap-1.5 font-semibold">
                                                    <Activity className="h-3.5 w-3.5 text-emerald-400" />
                                                    Live NAV
                                                </dt>
                                                <dd className="text-emerald-300 text-right font-mono text-base font-bold">
                                                    ${Number(p.live_nav_preview.nav_usd).toLocaleString(undefined, {
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </dd>
                                            </div>
                                        )}
                                        <div className="flex justify-between gap-4">
                                            <dt className="text-neutral-400 uppercase tracking-wider font-semibold">
                                                Target
                                            </dt>
                                            <dd className="text-right text-base font-bold" style={{ color: theme.hexHi }}>
                                                {p.target_return}
                                            </dd>
                                        </div>
                                        <div className="flex justify-between gap-4">
                                            <dt className="text-neutral-400 uppercase tracking-wider font-semibold">
                                                Track record
                                            </dt>
                                            <dd className="text-neutral-200 text-right text-sm leading-snug max-w-[65%]">
                                                {p.track_record}
                                            </dd>
                                        </div>
                                        <div className="flex justify-between gap-4">
                                            <dt className="text-neutral-400 uppercase tracking-wider font-semibold">
                                                Risk tier
                                            </dt>
                                            <dd className="text-neutral-200 text-right">
                                                {p.risk_tier}
                                            </dd>
                                        </div>
                                        <div className="flex justify-between gap-4">
                                            <dt className="text-neutral-400 uppercase tracking-wider font-semibold">
                                                Min ticket
                                            </dt>
                                            <dd className="text-neutral-100 text-right font-semibold">
                                                {p.min_ticket_usdt.toLocaleString()}{" "}
                                                USDT
                                            </dd>
                                        </div>
                                    </dl>
                                    <div className="mt-7 pt-5 border-t border-neutral-800 flex items-center justify-between">
                                        <span className="text-sm text-neutral-400">
                                            {p.lockup}
                                        </span>
                                        <Link
                                            to={`/dfi/${key}`}
                                            data-testid={`dfi-product-view-${key}`}
                                            className="inline-flex items-center gap-1.5 text-sm tracking-[0.18em] uppercase font-bold transition-colors"
                                            style={{ color: theme.hexHi }}
                                        >
                                            View live dashboard
                                            <ArrowUpRight className="h-4 w-4" />
                                        </Link>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* THE THESIS — 4 pillars */}
            <div className="border-t border-neutral-800 bg-neutral-950/50">
                <div className="max-w-6xl mx-auto px-6 py-20">
                    <div className="text-xs tracking-[0.3em] text-amber-300/70 uppercase mb-3">The DFI Thesis</div>
                    <h2 className="text-3xl sm:text-4xl font-light mb-10">Alpha without directional risk.</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {[
                            {t: "Delta-Neutral by Construction", d: "The flagship yield engine (Veda) runs four uncorrelated streams: Stable Earn, Levered stETH, Funding Arb, Covered Calls. Spot-long + perp-short structure. Market-neutral by design."},
                            {t: "Unified Operator Custody", d: "All client capital in one Bybit institutional master account. Operator-signed withdrawals only. Multi-sig on transfers > $50K. Cold-wallet reserve for the 2% self-insurance sidecar."},
                            {t: "Insured Capital · 3-5% APR Carve-Out", d: "Nexus Mutual for exchange custody. Sherlock / Chainproof for smart contracts. InsurAce for stablecoin / stETH depeg beyond 3% / 24h. 2% NAV USDC sidecar for kill-switch events."},
                            {t: "Chart-Honest Reporting", d: "Live PnL ledger · 60-second snapshot cadence · monthly NAV attestation · weekly client statement · full audit trail retained 90+ days."},
                        ].map((p, i) => (
                            <div key={i} className="border border-amber-300/20 bg-neutral-950 p-7 relative overflow-hidden">
                                <div className="absolute top-0 left-0 right-0 h-[2px] bg-amber-300" />
                                <h3 className="text-xl font-semibold text-amber-200 mb-3">{p.t}</h3>
                                <p className="text-sm text-neutral-300 leading-relaxed">{p.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* THE MATH */}
            <div className="border-t border-neutral-800">
                <div className="max-w-6xl mx-auto px-6 py-20">
                    <div className="text-xs tracking-[0.3em] text-amber-300/70 uppercase mb-3">The Math</div>
                    <h2 className="text-3xl sm:text-4xl font-light mb-3">The math that separates us.</h2>
                    <p className="text-neutral-400 italic mb-10">A yield number is only credible if it respects the structure of the market.</p>
                    <div className="border border-amber-300/20 divide-y divide-amber-300/10">
                        {[
                            {l: "Jim Simons · Medallion Fund", v: "66% APY", n: "Greatest trader in history.", w: "94%"},
                            {l: "Warren Buffett · Lifetime", v: "20% APY", n: "The compounding benchmark.", w: "29%"},
                            {l: "Veda · Backtest (16Q)", v: "33.05% CAGR", n: "Delta-neutral · 0.00% MaxDD · Sharpe 5.15", w: "47%"},
                            {l: "Veda · Live (90d)", v: "26.9% APY · 2.01%/mo", n: "Dead-centre of the 25-30% pitch band.", w: "38%"},
                            {l: "Arjuna · Live (40d · $100K)", v: "+179% · 14.74%/mo", n: "Spot 7-layer · live mainnet.", w: "85%"},
                            {l: "Kubera · Backtest (2.79y)", v: "+45.70% · 3.19%/mo", n: "Dual-engine fade · regime-guardrailed.", w: "65%"},
                        ].map((r, i) => (
                            <div key={i} className="grid grid-cols-12 gap-4 items-center px-6 py-4">
                                <div className="col-span-4 text-sm text-neutral-200">{r.l}</div>
                                <div className="col-span-3 h-2 bg-amber-300/10 rounded-full overflow-hidden">
                                    <div className="h-full bg-amber-300" style={{ width: r.w }} />
                                </div>
                                <div className="col-span-2 text-amber-200 font-bold text-right">{r.v}</div>
                                <div className="col-span-3 text-xs text-neutral-500 italic text-right">{r.n}</div>
                            </div>
                        ))}
                    </div>
                    <p className="mt-6 text-amber-300 italic text-base">Our top number sits above Buffett and below Simons. That is the only honest band.</p>
                </div>
            </div>

            {/* WHY BYBIT */}
            <div className="border-t border-neutral-800 bg-neutral-950/50">
                <div className="max-w-6xl mx-auto px-6 py-20">
                    <div className="text-xs tracking-[0.3em] text-amber-300/70 uppercase mb-3">Why Bybit</div>
                    <h2 className="text-3xl sm:text-4xl font-light mb-10">The rails underneath.</h2>
                    <div className="grid md:grid-cols-4 gap-5">
                        {[
                            {t: "Tier-1 Liquidity", d: "Top-3 global derivatives venue. Deepest BTC + ETH perp books outside Binance."},
                            {t: "Institutional Sub-Accounts", d: "Operator-signed withdrawals, trade-only API keys, LP-named segregation."},
                            {t: "Flexible Earn Rails", d: "Native USDC flexible savings + ETH staking at institutional rates."},
                            {t: "VARA-Licensed", d: "Dubai regulated. Clean onboarding for GCC and European LPs."},
                        ].map((b, i) => (
                            <div key={i} className="border border-amber-300/20 bg-neutral-950 p-6">
                                <h4 className="text-amber-200 font-semibold mb-3">{b.t}</h4>
                                <p className="text-sm text-neutral-400 leading-relaxed">{b.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* INSURANCE STACK */}
            <div className="border-t border-neutral-800">
                <div className="max-w-6xl mx-auto px-6 py-20">
                    <div className="text-xs tracking-[0.3em] text-amber-300/70 uppercase mb-3">Capital Protection</div>
                    <h2 className="text-3xl sm:text-4xl font-light mb-10">Four layers between your capital and loss.</h2>
                    <div className="border border-amber-300/20 divide-y divide-amber-300/10">
                        {[
                            {n: "01", t: "Exchange Custody · Nexus Mutual", d: "Covers Bybit exchange-level events: hack, custodial loss, platform insolvency. Cost carve-out 2-3% APR on protected NAV. Activates at $1M+ AUM tier."},
                            {n: "02", t: "Smart Contracts · Sherlock / Chainproof", d: "Covers Aave-v3 protocol exploit, smart-contract bug, oracle attack, governance attack. Cost 1-2% APR on Stream 2 NAV only."},
                            {n: "03", t: "Depeg Cover · InsurAce", d: "Covers USDC or stETH depeg beyond 3% for more than 24 hours. ~1% APR on affected stream NAV. Live at every AUM tier."},
                            {n: "04", t: "Operator Self-Insurance Sidecar", d: "2% NAV USDC reserve held in a segregated cold wallet. Ring-fenced for kill-switch trigger events smaller than external policy deductibles."},
                        ].map((l) => (
                            <div key={l.n} className="flex items-start gap-6 px-6 py-5">
                                <div className="text-3xl font-bold text-amber-300 tabular-nums w-14 shrink-0">{l.n}</div>
                                <div>
                                    <h4 className="text-amber-200 font-semibold mb-2">{l.t}</h4>
                                    <p className="text-sm text-neutral-400 leading-relaxed">{l.d}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ONBOARDING */}
            <div className="border-t border-neutral-800 bg-neutral-950/50">
                <div className="max-w-6xl mx-auto px-6 py-20">
                    <div className="text-xs tracking-[0.3em] text-amber-300/70 uppercase mb-3">Onboarding</div>
                    <h2 className="text-3xl sm:text-4xl font-light mb-10">72-hour activation.</h2>
                    <div className="space-y-5">
                        {[
                            {n: 1, t: "Day 1 · KYC / AML", d: "LP completes institutional KYC via Fireblocks + Shufti Pro. Term-sheet signed. Lloyd's policy co-named."},
                            {n: 2, t: "Day 1 · Sub-Account Creation", d: "Dedicated LP-named Bybit institutional sub-account provisioned. Withdrawal 2FA stays with LP."},
                            {n: 3, t: "Day 2 · Capital Transfer", d: "LP wires USDC / USDT or stable fiat conversion. Capital lands directly in LP-named account."},
                            {n: 4, t: "Day 2 · API Keys", d: "LP generates trade-only API keys. Keys scoped — no withdrawal, no transfer."},
                            {n: 5, t: "Day 3 · Strategy Deployment", d: "Capital allocated across Veda · Arjuna · Kubera per LP mandate. First trades execute."},
                        ].map((s) => (
                            <div key={s.n} className="flex items-start gap-5">
                                <div className="w-10 h-10 rounded-full bg-amber-300 text-neutral-950 flex items-center justify-center font-bold shrink-0">{s.n}</div>
                                <div className="pt-1">
                                    <h4 className="text-amber-200 font-semibold mb-1">{s.t}</h4>
                                    <p className="text-sm text-neutral-400">{s.d}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* RISK FRAMEWORK */}
            <div className="border-t border-neutral-800 bg-neutral-950/50">
                <div className="max-w-6xl mx-auto px-6 py-20">
                    <div className="text-xs tracking-[0.3em] text-amber-300/70 uppercase mb-3">Risk Framework</div>
                    <h2 className="text-3xl sm:text-4xl font-light mb-3">Guardrails written in code.</h2>
                    <p className="text-neutral-400 italic mb-10">Pre-trade, in-trade, post-trade — every risk is monitored and enforced automatically.</p>
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {t: "Pre-Trade", items: ["Position-size limits per asset, per venue, per strategy.", "Counterparty exposure caps — no single venue > 40% of AUM.", "Pre-approved asset whitelist · blacklist auto-triggered on illiquidity flags."]},
                            {t: "In-Trade", items: ["Real-time delta monitoring — residual beta > 0.1 auto-hedges within 90 seconds.", "Funding-rate circuit breakers — auto-exit if funding inverts beyond threshold.", "Latency and slippage caps enforced at order-router level."]},
                            {t: "Post-Trade", items: ["Daily PnL attribution per strategy, per asset, per venue.", "Reconciliation against exchange ledger — tolerance 0.01%.", "Weekly stress tests: 2020-COVID, 2022-FTX, 2023-SVB regimes replayed on the live book."]},
                        ].map((s) => (
                            <div key={s.t} className="border border-amber-300/20 bg-neutral-950 p-6">
                                <h4 className="text-amber-200 font-serif text-xl mb-4">{s.t}</h4>
                                <ul className="space-y-3">
                                    {s.items.map((it, i) => (
                                        <li key={i} className="flex items-start gap-3 text-sm text-neutral-300 leading-relaxed">
                                            <span className="h-1 w-1 rounded-full bg-amber-300 mt-2 shrink-0" />
                                            {it}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* TRACK RECORD */}
            <div className="border-t border-neutral-800">
                <div className="max-w-6xl mx-auto px-6 py-20">
                    <div className="text-xs tracking-[0.3em] text-amber-300/70 uppercase mb-3">Track Record</div>
                    <h2 className="text-3xl sm:text-4xl font-light mb-3">Audited net returns since inception.</h2>
                    <p className="text-neutral-400 italic mb-10">Live-paper and live-mainnet performance, fee-adjusted.</p>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-10">
                        {[
                            {v: "2.01%",  l: "Veda Monthly",      s: "live · compounded",   c: "#34d399"},
                            {v: "14.74%", l: "Arjuna Monthly",    s: "live · 40-day run",   c: "#f0d060"},
                            {v: "3.19%",  l: "Kubera Monthly",    s: "backtest · 2.79y",    c: "#c084fc"},
                            {v: "26.9%",  l: "Veda Annualized",   s: "90-day live",         c: "#34d399"},
                            {v: "+179%",  l: "Arjuna Annualized", s: "40d live tranche",    c: "#f0d060"},
                            {v: "+45.70%",l: "Kubera CAGR",       s: "2.79-year backtest",  c: "#c084fc"},
                        ].map((k) => (
                            <div key={k.l} className="border-2 bg-neutral-950 p-5 relative overflow-hidden" style={{ borderColor: `${k.c}40` }}>
                                <div className="absolute left-0 top-0 bottom-0 w-[4px]" style={{ background: k.c }} />
                                <div className="text-4xl font-bold tabular-nums" style={{ color: k.c }}>{k.v}</div>
                                <div className="text-sm tracking-[0.2em] uppercase text-neutral-100 mt-3 font-bold">{k.l}</div>
                                <div className="text-sm text-neutral-400 mt-1">{k.s}</div>
                            </div>
                        ))}
                    </div>
                    <div className="border border-amber-300/20 bg-neutral-950 p-6">
                        <svg viewBox="0 0 1000 220" className="w-full h-auto" preserveAspectRatio="none">
                            {[0.25, 0.5, 0.75].map((f, i) => (
                                <line key={i} x1="0" y1={220 * f} x2="1000" y2={220 * f} stroke="rgba(212,175,55,0.1)" strokeWidth="0.5" />
                            ))}
                            {(() => {
                                const n = 60;
                                const pts = Array.from({length: n+1}, (_, i) => {
                                    const t = i / n;
                                    const val = (Math.exp(0.22*t) - 1) / (Math.exp(0.22) - 1);
                                    const wobble = 0.015 * Math.sin(t*18) + 0.008 * Math.sin(t*33);
                                    const y_val = Math.max(0, Math.min(1, val + wobble));
                                    return `${(1000*t).toFixed(1)},${(220 - (0.08 + y_val*0.85)*220).toFixed(1)}`;
                                }).join(" ");
                                return <polyline points={pts} fill="none" stroke="#d4af37" strokeWidth="2.5" strokeLinecap="round" />;
                            })()}
                        </svg>
                        <div className="mt-3 flex items-center justify-between text-[10px] tracking-wide uppercase text-neutral-500">
                            <span>Blended Net Equity · Aug 2025 → Oct 2026</span>
                            <span>DFI live ledger · past performance not a guarantee</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* CLOSE / TAGLINE */}
            <div className="border-t border-neutral-800 bg-gradient-to-b from-neutral-950 to-black">
                <div className="max-w-6xl mx-auto px-6 py-24 text-center">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/downloads/DFI_logo_final.png`} alt="DFI" className="w-40 h-40 mx-auto drop-shadow-[0_0_40px_rgba(212,175,55,0.3)] mb-8" />
                    <h2 className="text-4xl sm:text-5xl font-serif text-amber-200 mb-4">Digital Finance Intelligence</h2>
                    <p className="text-amber-300/80 italic text-lg mb-2">Global Asset Management</p>
                    <div className="h-px w-24 bg-amber-300/60 mx-auto my-8" />
                    <p className="text-2xl font-serif italic text-neutral-200 max-w-2xl mx-auto mb-6">Where the honest math earns the keep.</p>
                    <p className="text-xs tracking-[0.4em] uppercase text-amber-300/80">Trust · Security · Transparency</p>
                    <div className="mt-12">
                        <Link to="/dfi/apply" className="inline-flex items-center gap-2 bg-amber-300 text-neutral-950 px-8 py-4 text-xs tracking-[0.25em] uppercase font-semibold hover:bg-amber-200">
                            Apply to subscribe <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                </div>
            </div>

            {/* Footer line */}
            <div className="border-t border-neutral-800">
                <div className="max-w-6xl mx-auto px-6 py-10 text-xs text-neutral-500 flex flex-wrap items-center justify-between gap-4">
                    <span>DFI · Digital Finance Intelligence · a distribution operator entity</span>
                    <span>Chart-honest · regulator-defensible · Saturn-clean</span>
                </div>
            </div>
        </div>
    );
}
